import { getFormattedDateTimeString } from '@/utils/dateTimeFormat';
import { FacilitySettingsModel } from '@/models/FacilitySettings';
import { MaintenancePriority } from '@/enums/MaintenancePriority';
import { MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import moment from "moment/moment";

export default {
  name: "MaintenanceDashboard",
  data() {
    return {
      loading: true,
      startDate: moment().subtract(1, 'month').toISOString().substring(0, 10),
      endDate: moment().toISOString().substring(0, 10),
      headers: [
        {
          text: 'Title',
          align: 'start',
          value: 'name',
        },
        {
          text: 'Assignee',
          align: 'start',
          value: 'assignee',
        },
        {
          text: 'Due date',
          align: 'start',
          value: 'estimatedCompletionDate',
          width: 120,
        }
      ],
      headersCancelledRequests: [
        {
          text: 'Title',
          align: 'start',
          value: 'name',
        },
        {
          text: 'Assignee',
          align: 'start',
          value: 'assignee',
        },
        {
          text: 'Cancelled date',
          align: 'start',
          value: 'cancelledDateTime',
          width: 140,
        }
      ]
    }
  },
  computed: {
    facility(): FacilitySettingsModel {
      return this.$store.state.facilityModule.selectedFacility;
    },
    maintenanceRequests() {
      return this.$store.state.maintenanceModule.maintenanceRequests
        .sort((a: any, b: any) => moment(a.estimatedCompletionDate).diff(moment(b.estimatedCompletionDate)))
        .map(x => {
          return {
            id: x.id,
            name: x.name ?? "-",
            assignee: x.assignee ?? "-",
            priority: x.priority ?? "Not prioritised",
            status: x.status ?? "-",
            category: x.category ?? "-",
            isHazardous: x.isHazardous ?? false,
            estimatedCompletionDate: moment(x.estimatedCompletionDate).format(this.facility.dateFormat) ?? "-",
            dateTime: x.dateTime ?? "-",
          }
        })
    },
    maintenanceRequestsLast30Days() {
      return this.maintenanceRequests.filter(x => {
        const dueDate = moment(x.dateTime.seconds * 1000).format('YYYY-MM-DD');
        const now = moment().subtract("day", 30).format('YYYY-MM-DD');
        return now <= dueDate;
      })
    },
    cancelledMaintenanceRequests() {
      return this.$store.state.maintenanceModule.closedMaintenanceRequests
        .filter(x => x.status == MaintenanceRequestStatus.Cancelled)
        .sort((a: any, b: any) => moment(a.cancelledDateTime.seconds * 1000).diff(moment(b.cancelledDateTime.seconds * 1000)))
        .map(x => {
          return {
            id: x.id,
            name: x.name ?? "-",
            assignee: x.assignee ?? "-",
            cancelledDateTime: moment(x.cancelledDateTime.seconds * 1000).format(this.facility.dateFormat) ?? "-",
          }
        })
    },
    urgentRequests() {
      return this.maintenanceRequests.filter(x => x.priority === MaintenancePriority.Urgent);
    },
    highRequests() {
      return this.maintenanceRequests.filter(x => x.priority === MaintenancePriority.High);
    },
    mediumRequests() {
      return this.maintenanceRequests.filter(x => x.priority === MaintenancePriority.Medium);
    },
    lowRequests() {
      return this.maintenanceRequests.filter(x => x.priority === MaintenancePriority.Low);
    },
    newMaintenanceRequests() {
      return this.maintenanceRequests.filter(x => x.status == MaintenanceRequestStatus.New)?.length;
    },
    inProgressMaintenanceRequests() {
      return this.maintenanceRequests.filter(x => x.status == MaintenanceRequestStatus.InProgress)?.length;
    },
    hazardousRequests() {
      return this.maintenanceRequests.filter(x => x.isHazardous);
    },
    overdueRequests() {
      return this.maintenanceRequests
        .filter(x => {
          const dueDate = moment(x.estimatedCompletionDate).format('YYYY-MM-DD');
          const now = moment().subtract("day", 1).format('YYYY-MM-DD');
          return now >= dueDate;
        })
    },
    dueIn7DaysRequests() {
      return this.maintenanceRequests
        .filter(x => {
          const dueDate = moment(x.estimatedCompletionDate).format('YYYY-MM-DD');
          const now = moment().format('YYYY-MM-DD');
          return now <= dueDate && moment(dueDate).diff(moment(now), 'days') <= 7;
        })
    },
    requestsByPriority() {
      return this.maintenanceRequests.reduce((acc, req) => {
          if (req?.priority) {
            acc[req.priority] = acc[req.priority] || 0;
            acc[req.priority] += 1;
          }

          return acc;
        }, {
          Urgent: 0,
          High: 0,
          Medium: 0,
          Low: 0,
        }
      );
    },
    requestsByPriorityColors() {
      const colors = {
        Urgent: '#5e0000',
        High: '#F44336',
        Medium: '#ff9800',
        Low: '#4CAF50',
      }

      return Object.keys(this.requestsByPriority).map(x => colors[x]);
    },
    requestsByCategory() {
      return this.maintenanceRequestsLast30Days.reduce((acc, req) => {
        if (req?.category) {
          acc[req.category] = acc[req.category] || 0;
          acc[req.category] += 1;
        }
        return acc;
      }, {});
    },
  },
  async created() {
    try {
      await Promise.all([
        this.$store.dispatch('getMaintenanceRequests'),
        this.$store.dispatch('getClosedMaintenanceRequests', {
          startDate: moment(this.startDate).startOf('day').toDate(),
          endDate: moment(this.endDate).endOf('day').toDate(),
        }),
      ]);
    } catch (error) {
      console.error('Error loading maintenance dashboard:', error);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
      if (!this.facility) return ''

      return getFormattedDateTimeString(date, this.facility.dateFormat, addTime ? this.facility.timeFormat : null, addYears);
    },
    openRequest({ id }) {
      this.$router.push(`/maintenanceRequests/${id}`);
    }
  }
}